import { Description, Dialog, DialogPanel } from "@headlessui/react";
import { FunctionComponent, useCallback, useMemo, useEffect } from "react";
import { useCookies } from "react-cookie";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";

import {
  CookiesBtn,
  ModalBackdrop,
  ModalContainer,
  StyledCookieButtonsArea,
  StyledCookieContainer,
  StyledCookieDescription,
  StyledCookieTitle,
} from "./styles";

import { configState, setModal } from "state/config-slice";
import { AppDispatch } from "state/reducers-combiner";
import getCookiePolicyLink from "utils/getCookiePolicyLink";
import { getSourceFromConfig } from "utils/getSource";
import FeatureWrapper from "components/FeatureToggle/FeatureWrapper";
import getFeature, { isFeatureActive } from "utils/getFeature";
import getOpco from "utils/getOpco";

const CookiesModal: FunctionComponent = () => {
  const { t } = useTranslation();
  const [, setCookie] = useCookies();
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const cookieLink = useMemo(() => getCookiePolicyLink(), []);
  const { modal } = useSelector(configState);
  const config = useSelector(configState);
  const url = config.openidUrls?.find((el) => el.url !== undefined)?.url;
  const ulfSource = url ? getSourceFromConfig(url) : false;

  const closeModal = useCallback(() => {
    getOpco() !== "de" && setCookie("SUBSCRIPTIONPLUS_TRACKING", false);
    setCookie("SUBSCRIPTIONPLUS_COOKIE_CONSENT_GRANTED", true);
    dispatch(setModal({ showModal: false }));
  }, [dispatch, setCookie]);

  useEffect(() => {
    if (pathname === "/cookie-preferences") {
      dispatch(setModal({ showModal: false }));
    }
  }, [dispatch, pathname]);

  return (
    <Dialog
      open={modal.showModal}
      onClose={() => {
        return;
      }}
      as={ModalContainer}
      data-testid="cookies-modal"
    >
      <ModalBackdrop data-testid="modal-backdrop" />

      <DialogPanel as={StyledCookieContainer} type="cookies">
        <Description as={StyledCookieTitle} data-testid={`cookies-modal-title`}>
          {t("Cookies & Tracking")}
        </Description>

        <Description
          as={StyledCookieDescription}
          data-testid={`cookies-modal-description`}
          dangerouslySetInnerHTML={{
            __html: t(
              "We use cookies and similar technologies to keep you logged in to this application and to monitor its performance. By accepting all cookies you are consenting to the use of performance cookies as detailed in our <a href='{{ href }}' target={'_blank'} rel='noreferrer'>Cookie Policy</a>",
              {
                href: cookieLink,
              },
            ),
          }}
        />

        <StyledCookieButtonsArea>
          <FeatureWrapper name={"Show cookies action btns"}>
            <>
              <CookiesBtn
                id="accept-cookies"
                onClick={() => {
                  setCookie("SUBSCRIPTIONPLUS_COOKIE_CONSENT_GRANTED", true);
                  setCookie("SUBSCRIPTIONPLUS_TRACKING", true);
                  if (ulfSource) {
                    setCookie("SOURCE", ulfSource);
                  }

                  dispatch(setModal({ showModal: false }));
                }}
                title={t("Accept & continue")}
              />
              <CookiesBtn
                id="manage-cookies"
                onClick={() => {
                  closeModal();
                  navigate("cookie-preferences");
                }}
                title={t("Manage Preferences")}
              />
              <CookiesBtn id="reject-cookies" onClick={closeModal} title={t("Reject optional cookies")} />
            </>
          </FeatureWrapper>
          <FeatureWrapper name="Extra cookie modal link btn">
            <CookiesBtn
              id="link-btn"
              onClick={() => {
                closeModal();
                window.open(getFeature("Extra cookie modal link btn")?.options.url, "_blank", "noopener,noreferrer");
              }}
              title={t("Imprint")}
            />
          </FeatureWrapper>
          {!isFeatureActive("Show cookies action btns") && (
            <CookiesBtn
              id="close-btn"
              onClick={() => {
                closeModal();
              }}
              title={t("Done")}
            />
          )}
        </StyledCookieButtonsArea>
      </DialogPanel>
    </Dialog>
  );
};

export default CookiesModal;
